import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  KeyRound,
  Plus,
  Pencil,
  Trash2,
  CircleCheck,
  TriangleAlert,
  Info,
} from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { KEYS_COPY } from '../../constants/partnerCopy';
import apiService from '../../services/apiService';
import styles from './MyLLMKeys.module.scss';

// Maps the `source` from /effective to a status badge (Partner tier guide §8.2).
// `partner` = the key comes from the partner that manages the user's company.
const sourceMeta = (partnerName) => ({
  user: { label: 'Your key', tone: 'success' },
  company: { label: "Your company's key", tone: 'info' },
  partner: { label: KEYS_COPY.sourcePartner(partnerName), tone: 'info' },
  platform: { label: 'Platform key', tone: 'info' },
  none: { label: 'Not available', tone: 'warning' },
});

// Cohere is a KB search re-ranking provider, not a chat model — "none" is not an
// error (search still works, just unranked), so its badges read softer/neutral.
const cohereSourceMeta = (partnerName) => ({
  user: { label: 'Re-ranking: your key', tone: 'success' },
  company: { label: 'Re-ranking: company key', tone: 'info' },
  partner: { label: `Re-ranking: ${KEYS_COPY.sourcePartner(partnerName)}`, tone: 'info' },
  platform: { label: 'Re-ranking: platform', tone: 'info' },
  none: { label: 'Re-ranking off', tone: 'neutral' },
});

const COHERE_HINT =
  'Search re-ranking (optional) — improves knowledge-base search quality. Not a chat model.';

// Shown for OpenAI when it has no usable key (no personal key, no company key,
// no platform fallback). OpenAI also powers Knowledge Base uploads/search, so
// without it those features stop working — worded for non-technical users.
const OPENAI_KB_BLOCKED_MSG =
  "Heads up: OpenAI also powers your Knowledge Base. Without OpenAI access you won't be able to upload files or use agents that search your documents. Add your own OpenAI key here, or ask your company admin to enable OpenAI.";

const MyLLMKeys = () => {
  const { isDarkMode } = useTheme();
  const { partner } = usePermissions();
  const partnerName = partner?.name;

  const [providers, setProviders] = useState([]);
  const [effective, setEffective] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Key editor dialog
  const [dialogProvider, setDialogProvider] = useState(null);
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [baseUrlValue, setBaseUrlValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Remove confirmation
  const [removeProvider, setRemoveProvider] = useState(null);
  const [removing, setRemoving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  const notify = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [providerData, effectiveData, credentialData] = await Promise.all([
        apiService.getProviders(true),
        apiService.getUserLLMKeysEffective(),
        apiService.getUserLLMKeys(),
      ]);
      setProviders(Array.isArray(providerData) ? providerData : providerData?.providers || []);
      setEffective(effectiveData?.providers || []);
      setCredentials(credentialData?.credentials || []);
    } catch (err) {
      console.error('Error loading LLM keys:', err);
      setError(err?.message || 'Failed to load your LLM keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Join providers × effective × credentials into one row per provider.
  const rows = useMemo(() => {
    const effectiveByProvider = new Map(effective.map((e) => [e.provider_id, e]));
    const credByProvider = new Map(credentials.map((c) => [c.provider_id, c]));
    return providers.map((provider) => {
      const eff = effectiveByProvider.get(provider.id);
      const cred = credByProvider.get(provider.id);
      return {
        providerId: provider.id,
        name: provider.display_name || provider.name,
        isCohere: String(provider.name || '').toLowerCase() === 'cohere',
        isOpenAI: String(provider.name || '').toLowerCase() === 'openai',
        baseUrl: provider.base_url || null,
        source: eff?.source || 'none',
        hasKey: !!cred?.has_key,
        keyLast4: cred?.key_last4 || null,
      };
    });
  }, [providers, effective, credentials]);

  const openEditor = (row) => {
    setDialogProvider(row);
    setApiKeyValue('');
    setBaseUrlValue(row.baseUrl || '');
  };

  const closeEditor = () => {
    if (saving) return;
    setDialogProvider(null);
    setApiKeyValue('');
    setBaseUrlValue('');
  };

  const handleSave = async () => {
    if (!dialogProvider || !apiKeyValue.trim()) return;
    try {
      setSaving(true);
      const payload = { api_key: apiKeyValue.trim() };
      if (baseUrlValue.trim()) payload.base_url = baseUrlValue.trim();
      await apiService.setUserLLMKey(dialogProvider.providerId, payload);
      notify(`${dialogProvider.name} key saved.`, 'success');
      setDialogProvider(null);
      setApiKeyValue('');
      setBaseUrlValue('');
      await fetchData();
    } catch (err) {
      console.error('Error saving key:', err);
      notify(err?.message || 'Failed to save key.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeProvider) return;
    try {
      setRemoving(true);
      await apiService.deleteUserLLMKey(removeProvider.providerId);
      notify(`${removeProvider.name} key removed.`, 'info');
      setRemoveProvider(null);
      await fetchData();
    } catch (err) {
      console.error('Error removing key:', err);
      notify(err?.message || 'Failed to remove key.', 'error');
    } finally {
      setRemoving(false);
    }
  };

  const renderBadge = (row) => {
    const table = row.isCohere ? cohereSourceMeta(partnerName) : sourceMeta(partnerName);
    const meta = table[row.source] || table.none;
    let Icon = CircleCheck;
    if (meta.tone === 'warning') Icon = TriangleAlert;
    else if (meta.tone === 'neutral') Icon = Info;
    return (
      <span className={`${styles.badge} ${styles[meta.tone]}`}>
        <Icon size={14} />
        {meta.label}
      </span>
    );
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.layout}>
        <Box className={styles.inner}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h4" component="h1">My LLM Keys</Typography>
            <Typography variant="body2">
              Set your own provider keys, or see which key each provider falls back to.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ borderRadius: '12px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : rows.length === 0 ? (
          <Box className={styles.emptyState}>
            <KeyRound className={styles.emptyIcon} size={56} />
            <Typography variant="h6">No providers available</Typography>
            <Typography variant="body2">There are no active LLM providers to configure yet.</Typography>
          </Box>
        ) : (
          <Box className={styles.tableCard}>
            <Box className={`${styles.row} ${styles.headRow}`}>
              <span>Provider</span>
              <span>Status</span>
              <span>Your key</span>
              <span className={styles.actionsHead}>Actions</span>
            </Box>
            {rows.map((row) => (
              <Box key={row.providerId} className={styles.row}>
                <Box className={styles.providerCell}>
                  <span className={styles.providerName}>{row.name}</span>
                  {row.isCohere ? (
                    <span className={styles.providerHint}>{COHERE_HINT}</span>
                  ) : (
                    row.baseUrl && <span className={styles.providerUrl}>{row.baseUrl}</span>
                  )}
                </Box>

                <Box className={styles.statusCell}>
                  {renderBadge(row)}
                  {row.source === 'none' && !row.isCohere && (
                    <span className={styles.helperText}>
                      <Info size={13} />
                      {row.isOpenAI ? OPENAI_KB_BLOCKED_MSG : 'Add your own key or ask your admin.'}
                    </span>
                  )}
                </Box>

                <Box className={styles.keyCell}>
                  {row.hasKey ? (
                    <span className={styles.keyMask}>••••{row.keyLast4}</span>
                  ) : (
                    <span className={styles.keyEmpty}>—</span>
                  )}
                </Box>

                <Box className={styles.actionsCell}>
                  {row.hasKey ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        className={styles.outlineBtn}
                        startIcon={<Pencil size={15} />}
                        onClick={() => openEditor(row)}
                      >
                        Update
                      </Button>
                      <IconButton
                        size="small"
                        className={styles.dangerIcon}
                        onClick={() => setRemoveProvider(row)}
                        title="Remove key"
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      className={styles.primaryBtn}
                      startIcon={<Plus size={15} />}
                      onClick={() => openEditor(row)}
                    >
                      Add my key
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        </Box>

        {/* Add / Update key dialog */}
        <Dialog
          open={!!dialogProvider}
          onClose={closeEditor}
          classes={{ paper: styles.dialogPaper }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className={styles.dialogTitle}>
            {dialogProvider?.hasKey ? 'Update' : 'Add'} {dialogProvider?.name} key
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <TextField
              autoFocus
              label="API key"
              type="password"
              fullWidth
              required
              variant="outlined"
              placeholder="Enter your API key"
              value={apiKeyValue}
              onChange={(e) => setApiKeyValue(e.target.value)}
              autoComplete="new-password"
              className={styles.formField}
              helperText={
                dialogProvider?.hasKey
                  ? 'Entering a new key replaces the existing one.'
                  : 'Your key is stored securely and never shown again.'
              }
            />
            <TextField
              label="Base URL (optional)"
              type="url"
              fullWidth
              variant="outlined"
              placeholder="https://..."
              value={baseUrlValue}
              onChange={(e) => setBaseUrlValue(e.target.value)}
              autoComplete="off"
              className={styles.formField}
            />
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button onClick={closeEditor} disabled={saving} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !apiKeyValue.trim()}
              className={styles.primaryBtn}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save key'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove confirmation */}
        <Dialog
          open={!!removeProvider}
          onClose={() => !removing && setRemoveProvider(null)}
          classes={{ paper: styles.dialogPaper }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className={styles.dialogTitle}>Remove key</DialogTitle>
          <DialogContent className={styles.deleteContent}>
            <Box className={styles.warningIconWrap}>
              <TriangleAlert />
            </Box>
            <Typography className={styles.deleteHeading}>
              Remove your {removeProvider?.name} key?
            </Typography>
            <Typography className={styles.deleteText}>
              This deletes your personal key for {removeProvider?.name}. The provider will fall back
              to your company's key, {partnerName ? `${partnerName}'s key, ` : ''}or the platform key if
              available.
            </Typography>
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button onClick={() => setRemoveProvider(null)} disabled={removing} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRemove}
              disabled={removing}
              className={styles.dangerButton}
              startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />}
            >
              {removing ? 'Removing...' : 'Remove key'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default MyLLMKeys;
